import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon,
  Landscape as LandscapeIcon,
  Agriculture as AgricultureIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const ClientManagement = () => {
  const { hasPermission, isSuperUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    companyName: '',
    plantationType: '',
    soilProfile: '',
    fertilizerType: '',
    fertilizerYears: '',
    plantationAge: '',
    location: '',
    contactNumber: '',
    email: '',
    notes: ''
  });
  const [expandedClient, setExpandedClient] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  // Check if user has permission to access this page
  const hasAccess = hasPermission('is_staff') || isSuperUser();

  // Plantation types
  const plantationTypes = [
    'Palm Oil',
    'Fruit Orchard',
    'Rubber',
    'Coconut',
    'Cocoa',
    'Mixed Crops',
    'Other'
  ];

  // Soil profile types
  const soilProfiles = [
    'Sandy',
    'Clay',
    'Loamy',
    'Silty',
    'Peaty',
    'Chalky',
    'Laterite',
    'Mixed'
  ];

  useEffect(() => {
    // Load client data from localStorage
    loadClients();
  }, []);

  const loadClients = () => {
    setLoading(true);
    setTimeout(() => {
      const savedClients = localStorage.getItem('clients');
      if (savedClients) {
        setClients(JSON.parse(savedClients));
      }
      setLoading(false);
    }, 500);
  };

  const handleOpenDialog = (type, client = null) => {
    if (type === 'edit' && client) {
      setCurrentClient(client);
      setFormData({ ...client });
    } else {
      setCurrentClient(null);
      setFormData({
        id: Date.now().toString(),
        name: '',
        companyName: '',
        plantationType: '',
        soilProfile: '',
        fertilizerType: '',
        fertilizerYears: '',
        plantationAge: '',
        location: '',
        contactNumber: '',
        email: '',
        notes: ''
      });
    }
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.plantationType || !formData.plantationAge) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Create or update client
      let updatedClients = [...clients];
      
      if (currentClient) {
        // Update existing client
        const index = clients.findIndex(c => c.id === currentClient.id);
        if (index !== -1) {
          updatedClients[index] = { ...formData };
        }
      } else {
        // Add new client
        updatedClients.push({ ...formData });
      }
      
      // Save to localStorage
      localStorage.setItem('clients', JSON.stringify(updatedClients));
      setClients(updatedClients);
      
      // Show success message
      setSuccess(currentClient ? 'Client updated successfully' : 'Client added successfully');
      setTimeout(() => setSuccess(''), 3000);
      
      // Close dialog
      handleCloseDialog();
    } catch (error) {
      setError('Error saving client: ' + error.message);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      const updatedClients = clients.filter(client => client.id !== id);
      localStorage.setItem('clients', JSON.stringify(updatedClients));
      setClients(updatedClients);
      setSuccess('Client deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleToggleExpand = (clientId) => {
    setExpandedClient(expandedClient === clientId ? null : clientId);
  };

  if (!hasAccess) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1">
          You don't have permission to manage clients.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Client Management</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
        >
          Add Client
        </Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : clients.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No clients found. Add your first client to get started.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>Client/Company Name</TableCell>
                <TableCell>Plantation Type</TableCell>
                <TableCell>Age (Years)</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client) => (
                <React.Fragment key={client.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleToggleExpand(client.id)}>
                        {expandedClient === client.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body1">{client.name}</Typography>
                          {client.companyName && (
                            <Typography variant="body2" color="textSecondary">
                              {client.companyName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<AgricultureIcon />}
                        label={client.plantationType}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{client.plantationAge} years</TableCell>
                    <TableCell>{client.location}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => handleOpenDialog('edit', client)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(client.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                      <Collapse in={expandedClient === client.id} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 3, backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom color="primary">
                                Estate Details
                              </Typography>
                              <Box sx={{ pl: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Soil Profile:</strong> {client.soilProfile || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Fertilizer Type:</strong> {client.fertilizerType || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Fertilizer Years:</strong> {client.fertilizerYears || 'N/A'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom color="primary">
                                Contact Information
                              </Typography>
                              <Box sx={{ pl: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Phone:</strong> {client.contactNumber || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Email:</strong> {client.email || 'N/A'}
                                </Typography>
                                {client.notes && (
                                  <Typography variant="body2" gutterBottom>
                                    <strong>Notes:</strong> {client.notes}
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentClient ? 'Edit Client' : 'Add New Client'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
              Client Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoFocus
                  label="Client Name *"
                  name="name"
                  fullWidth
                  value={formData.name}
                  onChange={handleChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company Name"
                  name="companyName"
                  fullWidth
                  value={formData.companyName}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact Number"
                  name="contactNumber"
                  fullWidth
                  value={formData.contactNumber}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Location/Address"
                  name="location"
                  fullWidth
                  value={formData.location}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Plantation Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="plantation-type-label">Plantation Type *</InputLabel>
                  <Select
                    labelId="plantation-type-label"
                    name="plantationType"
                    value={formData.plantationType}
                    onChange={handleChange}
                    required
                    label="Plantation Type *"
                  >
                    {plantationTypes.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Plantation Age (Years) *"
                  name="plantationAge"
                  type="number"
                  fullWidth
                  value={formData.plantationAge}
                  onChange={handleChange}
                  required
                  margin="normal"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">years</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="soil-profile-label">Soil Profile</InputLabel>
                  <Select
                    labelId="soil-profile-label"
                    name="soilProfile"
                    value={formData.soilProfile}
                    onChange={handleChange}
                    label="Soil Profile"
                  >
                    {soilProfiles.map(soil => (
                      <MenuItem key={soil} value={soil}>{soil}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fertilizer Type"
                  name="fertilizerType"
                  fullWidth
                  value={formData.fertilizerType}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Years of Fertilizer Use"
                  name="fertilizerYears"
                  type="number"
                  fullWidth
                  value={formData.fertilizerYears}
                  onChange={handleChange}
                  margin="normal"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">years</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <TextField
              label="Notes"
              name="notes"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
            >
              {currentClient ? 'Update Client' : 'Add Client'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ClientManagement; 